/**
 * Timezone utility functions for handling UTC timestamps and local timezone display
 */

export interface TimezoneInfo {
  timezone: string;
  offset: string;
  location: string;
}

// Common timezones for your use case
export const COMMON_TIMEZONES: Record<string, TimezoneInfo> = {
  'America/Toronto': { timezone: 'America/Toronto', offset: 'EST/EDT', location: 'Toronto, Canada' },
  'America/Vancouver': { timezone: 'America/Vancouver', offset: 'PST/PDT', location: 'Vancouver, Canada' },
  'America/Edmonton': { timezone: 'America/Edmonton', offset: 'MST/MDT', location: 'Edmonton, Canada' },
  'America/Winnipeg': { timezone: 'America/Winnipeg', offset: 'CST/CDT', location: 'Winnipeg, Canada' },
  'Asia/Kolkata': { timezone: 'Asia/Kolkata', offset: 'IST', location: 'India' },
  'UTC': { timezone: 'UTC', offset: 'UTC', location: 'UTC' },
};

/**
 * Get user's browser timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format UTC timestamp to local timezone
 */
export const formatToLocalTime = (
  utcTimestamp: string,
  timezone?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  // Ensure timestamp is treated as UTC
  const normalizedTimestamp = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(normalizedTimestamp);
  const targetTimezone = timezone || getUserTimezone();
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: targetTimezone,
  };

  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format UTC timestamp to relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (
  utcTimestamp: string,
  timezone?: string
): string => {
  // Ensure timestamp is treated as UTC
  const normalizedTimestamp = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(normalizedTimestamp);
  const now = new Date();
  
  // Calculate the difference in milliseconds (this is timezone-independent)
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    // For older dates, show the actual date
    return formatToLocalTime(utcTimestamp, timezone, {
      month: 'short',
      day: 'numeric',
      year: diffInDays > 365 ? 'numeric' : undefined,
    });
  }
};

/**
 * Format UTC timestamp for notifications (compact format)
 */
export const formatNotificationTime = (
  utcTimestamp: string,
  timezone?: string
): string => {
  // Ensure timestamp is treated as UTC
  const normalizedTimestamp = utcTimestamp.endsWith('Z') ? utcTimestamp : utcTimestamp + 'Z';
  const date = new Date(normalizedTimestamp);
  const now = new Date();
  
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // Show relative time for recent notifications
  if (diffInHours < 24) {
    return formatRelativeTime(utcTimestamp, timezone);
  } else if (diffInDays < 7) {
    return formatToLocalTime(utcTimestamp, timezone, {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } else {
    return formatToLocalTime(utcTimestamp, timezone, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }
};

/**
 * Get timezone offset string (e.g., "UTC-5", "UTC+5:30")
 */
export const getTimezoneOffset = (timezone: string): string => {
  const now = new Date();
  const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
  const targetTime = new Date(utc.toLocaleString('en-US', { timeZone: timezone }));
  const offset = (targetTime.getTime() - utc.getTime()) / (1000 * 60 * 60);
  
  if (offset === 0) return 'UTC';
  if (offset > 0) return `UTC+${offset}`;
  return `UTC${offset}`;
};

/**
 * Validate if a timezone string is valid
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Get all available timezones grouped by region
 */
export const getAvailableTimezones = (): Record<string, TimezoneInfo[]> => {
  const regions: Record<string, TimezoneInfo[]> = {
    'North America': [],
    'Asia': [],
    'Europe': [],
    'Other': [],
  };

  Object.values(COMMON_TIMEZONES).forEach(tz => {
    if (tz.timezone.startsWith('America/')) {
      regions['North America'].push(tz);
    } else if (tz.timezone.startsWith('Asia/')) {
      regions['Asia'].push(tz);
    } else if (tz.timezone.startsWith('Europe/')) {
      regions['Europe'].push(tz);
    } else {
      regions['Other'].push(tz);
    }
  });

  return regions;
};
